import { Link } from 'react-router-dom';
import { MessagesSquare, ExternalLink } from 'lucide-react';
import { useEpicRequestInterview } from '@/hooks/queries/use-epic-requests';

/**
 * LinkedInterviewLink (ENG-147) — the ER → interview navigation affordance.
 *
 * When an epic request has a linked interview (ENG-146 set `Interview.epicRequestId`
 * on completion), this renders a "View interview" deep-link to the driving
 * mailbox job at `/mailbox/jobs/<jobId>`, closing the navigation loop with the
 * reverse link on the job page (interview-flow-panel).
 *
 * The linkage endpoint is OWNER-SCOPED (only the ER creator or a global admin
 * may read it), so callers pass `enabled` (typically `isCreator`) to avoid
 * firing a request that would 403 for team members viewing someone else's ER.
 * Renders nothing while loading, on error, when no interview is linked, or when
 * the linked interview has no driving job (nothing to navigate to).
 */
export function LinkedInterviewLink({
  requestId,
  enabled = true,
}: {
  requestId: string;
  enabled?: boolean;
}) {
  const { data: interview } = useEpicRequestInterview(requestId, { enabled });

  if (!interview?.jobId) return null;

  return (
    <div className="border-t border-white/[0.06] px-4 md:px-6 py-4">
      <div className="flex items-center gap-2 mb-2">
        <MessagesSquare className="h-4 w-4 text-purple-300" />
        <span className="text-sm font-semibold text-purple-300">Conversation</span>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        The chat that wrote this request. Keep talking there to add detail or raise its score.
      </p>
      <Link
        to={`/mailbox/jobs/${interview.jobId}`}
        className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-sm hover:bg-white/[0.06] transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5 text-purple-300" />
        <span className="font-medium">Open conversation</span>
      </Link>
    </div>
  );
}
